import { useRef, useEffect, useMemo } from 'react';
import { useTVBoard, createTVBoardPages } from '../context/TVBoardContext';
import { MoldOrderFirstLevelType } from '../types/mold';
import { BoardColumn } from '@/models/appState';

/**
 * Shared hook for TV board functionality across all board components
 * Simplifies and centralizes TV-related logic
 */
export const useTVBoardLogic = (
  displayMode: 'tv' | 'default',
  boardType: MoldOrderFirstLevelType,
  visibleColumns: BoardColumn[],
) => {
  const kanbanScrollRef = useRef<HTMLDivElement>(null);
  
  // TV Board context (we need to call hooks unconditionally)
  // Only attempt to use the context when in TV mode
  let rawTVBoardContext: ReturnType<typeof useTVBoard> | null = null;
  
  try {
    // Always call the hook to maintain hook call order
    rawTVBoardContext = useTVBoard();
  } catch {
    // Not in TVBoardProvider context - this is expected when displayMode !== 'tv'
    rawTVBoardContext = null;
  }
  
  // Only use the context when actually in TV mode
  const tvBoardContext = displayMode === 'tv' ? rawTVBoardContext : null;

  // Set up TV pagination/scroll when in TV mode
  useEffect(() => {
    if (displayMode === 'tv' && tvBoardContext && visibleColumns.length > 0) {
      const pages = createTVBoardPages(boardType, visibleColumns.length, 4, tvBoardContext.displayMode);
      tvBoardContext.setPages(pages);
    }
  }, [displayMode, boardType, visibleColumns.length, tvBoardContext?.displayMode, tvBoardContext?.setPages]);

  // Handle scroll effect for scroll mode
  useEffect(() => {
    if (displayMode === 'tv' && tvBoardContext?.displayMode === 'scroll' && tvBoardContext.currentPage && kanbanScrollRef.current) {
      // Use a small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        const container = kanbanScrollRef.current;
        if (!container) return;
        
        const columnWidth = 320 + 16; // Column width + gap
        const startIndex = tvBoardContext.currentPage?.columnStartIndex || 0;
        const scrollPosition = startIndex * columnWidth;
        

        
        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }, 50); // Small delay to ensure DOM is ready
      
      return () => clearTimeout(timeoutId);
    }
  }, [displayMode, tvBoardContext?.displayMode, tvBoardContext?.currentPage?.id]);

  // Get columns to display based on mode
  const getDisplayColumns = useMemo(() => {
    return (allColumns: BoardColumn[]) => {
      if (displayMode === 'tv' && tvBoardContext?.currentPage) {
        // For scroll mode (default), show all columns (scrolling will be handled by CSS)
        if (tvBoardContext.displayMode === 'scroll') {
          return visibleColumns;
        }
        
        // For pagination mode, slice the columns to show only the current page
        const { columnStartIndex = 0, columnEndIndex } = tvBoardContext.currentPage;
        const endIndex = columnEndIndex !== undefined ? columnEndIndex + 1 : columnStartIndex + 4;
        return visibleColumns.slice(columnStartIndex, Math.min(endIndex, visibleColumns.length));
      }
      return allColumns;
    };
  }, [displayMode, tvBoardContext?.currentPage?.id, tvBoardContext?.displayMode]);

  return {
    kanbanScrollRef,
    tvBoardContext,
    getDisplayColumns,
  };
};

export default useTVBoardLogic;